import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { FILTER_DEFINITIONS, type PageFilters, REPORT_VIEWS, type ReportView, type Row, filtersError } from './model';

const VISIBLE_ROWS = 25;

export function csConversationsPageView(input: {
  ready: boolean;
  filters: PageFilters;
  options: Record<string, Array<string>>;
  period: string;
  totals: Row | null;
  rows: Array<Row>;
  truncated: boolean;
}): PageView {
  const { filters, options } = input;
  const own = FILTER_DEFINITIONS.filter((definition) => definition.view === filters.reportView);
  const filterSchema: Record<string, PageField> = {
    reportView: { type: 'enum', values: REPORT_VIEWS.map((view) => view.value) },
    from: { type: 'date' },
    to: { type: 'date' },
    ...Object.fromEntries(own.map(({ key }) => [key, (options[key] ?? []).length > 100
      ? { type: 'text', multiple: true, maxSelections: 200, maxLength: 4000 }
      : { type: 'enum', multiple: true, maxSelections: 200, values: ['', ...(options[key] ?? []).filter((value) => !value.includes(','))] }])),
  };
  return {
    title: 'CS Conversations & Sales',
    ready: input.ready,
    dataMode: 'live lux-analytics-prod.analytics CS models',
    reportView: REPORT_VIEWS.find((view) => view.value === filters.reportView)?.label,
    filters: {
      reportView: filters.reportView,
      from: filters.from,
      to: filters.to,
      ...Object.fromEntries(
        own.map(({ key }) => [key, filters.selections[key].join(',')]),
      ),
    },
    filterSchema,
    activeSelections: Object.fromEntries(
      own.filter(({ key }) => filters.selections[key].length).map(({ key }) => [key, filters.selections[key]]),
    ),
    period: input.period,
    totals: input.totals,
    visibleSections: [
      {
        title: REPORT_VIEWS.find((view) => view.value === filters.reportView)?.label,
        totalRows: input.rows.length,
        rows: input.rows.slice(0, VISIBLE_ROWS),
        truncated: input.truncated || input.rows.length > VISIBLE_ROWS,
      },
    ],
    definitions: {
      source:
        'lux-analytics-prod.analytics cs_conversations_with_sales, cs_orders_with_conversion, cs_conversations_excluded and cs_interactions_conversions_agg, queried live; rebuilt daily.',
      dates:
        'Sydney dates: adjusted conversation start for conversations, order date for orders and the Conversion Rate Report.',
      measures:
        'Sales Conversion Rate = SUM(conversions) / SUM(interaction_score_adjusted). Excluded Interactions % = SUM(excluded) / SUM(total interactions). Ratios divide period sums.',
      teamLeader: 'Team Leader is the roster pod leader for the agent today, not a historical assignment.',
      privacy: 'Customer email, contact number and customer_indicator are not loaded.',
      filters:
        'Filters apply to their own tab. Use comma-separated listed values, or an empty string for all. Values containing commas cannot be set here.',
    },
  };
}

export function editFromBridge(
  changes: Record<string, unknown>,
  view: PageView,
  filters: PageFilters,
  options: Record<string, Array<string>>,
): Partial<PageFilters> {
  const validation = { ...view.filterSchema };
  for (const { key } of FILTER_DEFINITIONS.filter((definition) => definition.view === filters.reportView)) {
    validation[key] = { type: 'enum', multiple: true, maxSelections: 200, values: ['', ...(options[key] ?? []).filter((value) => !value.includes(','))] };
  }
  validatePageFilters(changes, validation);
  const next: Partial<PageFilters> = {};
  if ('reportView' in changes) {
    next.reportView = changes.reportView as ReportView;
  }
  if ('from' in changes) {
    next.from = changes.from as string;
  }
  if ('to' in changes) {
    next.to = changes.to as string;
  }
  const selections = { ...filters.selections };
  let changed = false;
  for (const { key } of FILTER_DEFINITIONS) {
    if (!(key in changes)) {
      continue;
    }
    const value = changes[key] as string;
    selections[key] = value.split(',').map((item) => item.trim()).filter(Boolean);
    changed = true;
  }
  if (changed) {
    next.selections = selections;
  }
  const error = filtersError({ ...filters, ...next });
  if (error) throw new Error(error);
  return next;
}
